import { Settings2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { StorageWizard } from './StorageWizard'
import type { ApiStorageNode } from '@/types/storage'

/**
 * The setup wizard as a modal.
 *
 * Inline, it pushed the units table and everything else off the screen while
 * you worked, and there was no clear boundary between "describing a layout"
 * and "browsing one". A dialog makes the task modal because it is: nothing is
 * created until the review step, and until then the page behind is unchanged.
 *
 * Height is bounded with a scrolling body, so a four-level plan with a large
 * matrix preview cannot push the Back/Review controls off the bottom — the
 * failure the label dialog had.
 */
export function StorageWizardDialog({
  root,
  open,
  extending,
  onClose,
}: {
  root: ApiStorageNode | null
  open: boolean
  /** True when the unit already has levels — re-running only fills in gaps. */
  extending: boolean
  onClose: () => void
}) {
  return (
    <Dialog open={open && root !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='flex max-h-[88vh] flex-col gap-0 p-0 sm:max-w-2xl'>
        <DialogHeader className='shrink-0 space-y-1 border-b px-6 pb-4 pt-5'>
          <DialogTitle className='flex items-center gap-2'>
            <Settings2 className='h-5 w-5 text-primary' />
            Set up {root?.name ?? root?.code}
          </DialogTitle>
          <DialogDescription>
            {extending
              ? 'Anything that already exists is left alone — only what is missing gets created.'
              : 'Describe what is inside, one level at a time. Nothing is created until you confirm.'}
          </DialogDescription>
        </DialogHeader>

        <div className='min-h-0 flex-1 overflow-y-auto px-6 py-4'>
          {root && (
            <StorageWizard
              root={root}
              extending={extending}
              embedded
              onDone={onClose}
              onCancel={onClose}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
